import {
  Zap, Paintbrush, Square, Wind, Droplet,
  Home, Trees, ChefHat, Building2, Hammer,
} from "lucide-react";

const servicesData = [
  {
    id: "electrical",
    slug: "electrical",
    title: "Electrical",
    icon: Zap,
    shortDesc: "Electrical work and wiring",
    heroImage: "https://images.unsplash.com/photo-1660330589693-99889d60181e?auto=format&fit=crop&w=1200&q=80",
    metaTitle: "Electrical Contractors Near You | Free Estimates — MyProjectBidder",
    metaDesc: "Get free estimates from licensed, insured electricians in your area. No spam — your contact goes to one contractor at a time. Compare bids and hire with confidence.",
    heroHeadline: "Don't Gamble on Wiring — Get Quotes from Licensed, Insured Electricians",
    heroSub: "Faulty wiring is a leading cause of home fires. Whether it's a panel upgrade, new outlets, or a full rewire, get it done right by verified pros.",
    trustPoints: [
      "Every electrician is licensed and carries current insurance",
      "Background-checked professionals only",
      "Your contact goes to ONE contractor — no spam calls",
      "Real local reviews from verified homeowners",
    ],
    process: [
      { step: "01", title: "Describe Your Electrical Project", desc: "Tell us what you need — panel upgrade, rewiring, new outlets, lighting install, or troubleshooting." },
      { step: "02", title: "Receive Electrician Estimates", desc: "Licensed electricians in your area review your project and send competitive bids." },
      { step: "03", title: "Choose Your Electrician", desc: "Compare estimates side-by-side, read verified reviews, and hire with confidence." },
    ],
    included: [
      "Panel upgrades and replacements",
      "Whole-home rewiring",
      "Outlet and switch installation",
      "Lighting fixture installation",
      "Ceiling fan wiring",
      "Code violation corrections",
      "EV charger installation",
      "Smoke and CO detector wiring",
    ],
    persuasiveCopy: "Faulty or outdated wiring is a leading cause of residential fires in the U.S. DIY or unlicensed electrical work doesn't just risk your family's safety — it can fail home inspections and tank your resale value. Every electrician on MyProjectBidder is licensed, insured, and verified. Post your project in two minutes, get real bids from real pros, and know the job will pass inspection the first time.",
    faqs: [
      { q: "How much does electrical work typically cost?", a: "Minor work like adding outlets runs $150–$500. Panel upgrades range $1,500–$4,000. Full rewires for older homes can run $8,000–$15,000+ depending on square footage and complexity." },
      { q: "How long does electrical work take?", a: "Simple jobs take a few hours. Panel upgrades are typically a full day. Whole-home rewires can take 1–2 weeks depending on the size of the home and accessibility." },
      { q: "Do I need a permit for electrical work?", a: "Most electrical work beyond simple fixture swaps requires a permit. Your licensed electrician will know exactly what's needed and can pull permits on your behalf." },
      { q: "How do estimates work on MyProjectBidder?", a: "Post your project details for free. Licensed electricians in your area review it and send you their bids. You compare side-by-side and choose. No obligation, no spam." },
    ],
  },
  {
    id: "painting",
    slug: "painting",
    title: "Painting",
    icon: Paintbrush,
    shortDesc: "Interior and exterior painting",
    heroImage: "https://images.unsplash.com/photo-1688372199140-cade7ae820fe?auto=format&fit=crop&w=1200&q=80",
    metaTitle: "Painting Contractors Near You | Free Estimates — MyProjectBidder",
    metaDesc: "Get free estimates from professional painters in your area. Interior, exterior, and cabinet painting. No spam, no upfront fees.",
    heroHeadline: "The Single Highest-ROI Project You Can Do This Month",
    heroSub: "Fresh paint is consistently cited as one of the highest-return renovations for resale value and curb appeal. Get it done by pros who won't leave drips on your floors.",
    trustPoints: [
      "Professional painters with verified portfolios",
      "Licensed, insured, and background-checked",
      "Your info goes to ONE painter — no cold calls",
      "Honest reviews from real homeowners",
    ],
    process: [
      { step: "01", title: "Describe Your Paint Job", desc: "Tell us the scope — interior rooms, exterior, cabinets, trim, or a full repaint." },
      { step: "02", title: "Receive Painter Estimates", desc: "Local painting pros review your project and send you their best price." },
      { step: "03", title: "Choose Your Painter", desc: "Compare bids, check reviews, and hire the painter who fits your budget and timeline." },
    ],
    included: [
      "Interior wall and ceiling painting",
      "Exterior house painting",
      "Cabinet painting and refinishing",
      "Trim, door, and baseboard painting",
      "Deck and fence staining",
      "Wallpaper removal and prep",
      "Popcorn ceiling removal",
      "Color consultation",
    ],
    persuasiveCopy: "A fresh coat of paint delivers one of the highest returns on investment of any home improvement project — up to 107% ROI on interior painting and massive curb appeal boosts from exterior work. It's fast, it's visible, and it transforms how a home looks and feels. The difference between a DIY weekend and a professional job? Clean lines, proper prep, and a finish that lasts years instead of months. Get real bids from verified painters today.",
    faqs: [
      { q: "How much does house painting cost?", a: "Interior painting runs $2–$6 per sq ft. A typical bedroom costs $300–$750. Whole-home interior: $3,000–$8,000. Exterior: $3,000–$12,000 depending on size and prep needed." },
      { q: "How long does a paint job take?", a: "A single room takes 1–2 days. A full interior can take 3–7 days. Exterior painting typically takes 4–7 days depending on weather and prep work." },
      { q: "Do I need to move furniture?", a: "Professional painters typically move and cover furniture as part of their prep. Confirm with your chosen contractor what's included in their bid." },
      { q: "How do I get started?", a: "Post your project on MyProjectBidder for free. Describe the rooms, surfaces, and any special requirements. Local painters will send you competitive bids." },
    ],
  },
  {
    id: "flooring",
    slug: "flooring",
    title: "Flooring",
    icon: Square,
    shortDesc: "Floor installation and refinishing",
    heroImage: "https://images.unsplash.com/photo-1731185752376-a4cf3e8556fa?auto=format&fit=crop&w=1200&q=80",
    metaTitle: "Flooring Contractors Near You | Free Estimates — MyProjectBidder",
    metaDesc: "Get free estimates from local flooring installers. Hardwood, tile, LVP, carpet, and more. No spam, verified contractors only.",
    heroHeadline: "Floors Take Daily Abuse — Get It Installed Right the First Time",
    heroSub: "New flooring is one of the most noticed upgrades by buyers and guests. The wrong installer means uneven, squeaking, or failing floors within a year.",
    trustPoints: [
      "Experienced flooring installers with verified work",
      "Licensed and fully insured",
      "No cold calls — your info stays private",
      "Real reviews from homeowners in your area",
    ],
    process: [
      { step: "01", title: "Describe Your Flooring Project", desc: "Tell us the rooms, square footage, and material you're considering — hardwood, tile, LVP, or carpet." },
      { step: "02", title: "Receive Flooring Estimates", desc: "Local flooring pros review your project and send detailed bids." },
      { step: "03", title: "Choose Your Installer", desc: "Compare pricing, timelines, and reviews. Hire the installer you trust." },
    ],
    included: [
      "Hardwood floor installation",
      "Luxury vinyl plank (LVP) installation",
      "Tile and stone flooring",
      "Carpet installation",
      "Hardwood refinishing and sanding",
      "Subfloor repair and leveling",
      "Baseboard and transition installation",
      "Heated floor systems",
    ],
    persuasiveCopy: "Your floors take more abuse than any other surface in your home — foot traffic, pets, spills, furniture. New flooring is one of the first things buyers and guests notice, and the wrong installer means uneven seams, squeaking planks, or failing adhesive within a year. Every flooring contractor on MyProjectBidder is verified, insured, and reviewed by real homeowners. Post your project, compare bids, and get floors that last.",
    faqs: [
      { q: "How much does new flooring cost?", a: "Costs vary by material: LVP runs $3–$7/sq ft installed, hardwood $6–$12/sq ft, tile $7–$15/sq ft, carpet $2–$5/sq ft. A 500 sq ft room typically runs $1,500–$6,000." },
      { q: "How long does flooring installation take?", a: "A single room takes 1–2 days. A whole home can take 3–7 days. Hardwood refinishing adds 2–3 days for sanding and curing." },
      { q: "Should I do hardwood or LVP?", a: "LVP is more affordable and water-resistant — great for kitchens and basements. Hardwood adds more resale value and has a premium feel. Your contractor can help you decide based on your space." },
      { q: "How do estimates work?", a: "Post your project details for free on MyProjectBidder. Local flooring installers review it and send competitive bids. Compare and choose — no obligation." },
    ],
  },
  {
    id: "hvac",
    slug: "hvac",
    title: "HVAC",
    icon: Wind,
    shortDesc: "Heating, ventilation, and AC",
    heroImage: "https://images.unsplash.com/photo-1642749776312-aa42ce20c9f5?auto=format&fit=crop&w=1200&q=80",
    metaTitle: "HVAC Contractors Near You | Free Estimates — MyProjectBidder",
    metaDesc: "Get free estimates from local HVAC technicians for heating, cooling, and ventilation. Licensed pros, no spam, verified reviews.",
    heroHeadline: "Stop Overpaying to Heat and Cool Your Home",
    heroSub: "An inefficient or failing HVAC system spikes utility costs and can leave your home without heat or AC during extreme weather. Don't wait for a mid-summer breakdown.",
    trustPoints: [
      "HVAC technicians with current certifications",
      "Licensed, bonded, and insured",
      "One contractor at a time — no spam calls",
      "Verified reviews from local homeowners",
    ],
    process: [
      { step: "01", title: "Describe Your HVAC Needs", desc: "Tell us what's happening — broken AC, furnace replacement, duct work, or a new system install." },
      { step: "02", title: "Receive HVAC Estimates", desc: "Certified HVAC technicians in your area review your project and send bids." },
      { step: "03", title: "Choose Your Technician", desc: "Compare pricing, warranties, and reviews. Hire the HVAC pro who fits your needs." },
    ],
    included: [
      "AC installation and replacement",
      "Furnace installation and repair",
      "Heat pump systems",
      "Ductwork installation and repair",
      "Thermostat installation",
      "HVAC maintenance and tune-ups",
      "Indoor air quality systems",
      "Mini-split and ductless systems",
    ],
    persuasiveCopy: "Your HVAC system accounts for nearly half of your home's energy bill. An aging or undersized system doesn't just cost you in comfort — it costs you hundreds of extra dollars every year in wasted energy. And when it fails in July or January, emergency service calls come with emergency pricing. Get ahead of it. Post your HVAC project on MyProjectBidder, compare bids from certified technicians, and invest in a system that pays for itself in lower utility bills.",
    faqs: [
      { q: "How much does a new HVAC system cost?", a: "A full system replacement (AC + furnace) typically runs $5,000–$12,000. AC-only replacement: $3,000–$7,000. Repairs and tune-ups: $100–$500." },
      { q: "How long does HVAC installation take?", a: "A straightforward replacement takes 1–2 days. New ductwork or system changes can take 3–5 days." },
      { q: "How often should HVAC be serviced?", a: "Twice a year is recommended — once before cooling season and once before heating season. Regular maintenance extends system life and prevents breakdowns." },
      { q: "How do I get estimates?", a: "Post your project details on MyProjectBidder for free. HVAC technicians in your area will send competitive bids for you to compare." },
    ],
  },
  {
    id: "plumbing",
    slug: "plumbing",
    title: "Plumbing",
    icon: Droplet,
    shortDesc: "Plumbing installation and repair",
    heroImage: "https://images.unsplash.com/photo-1768321916212-17ae334a3d63?auto=format&fit=crop&w=1200&q=80",
    metaTitle: "Plumbing Contractors Near You | Free Estimates — MyProjectBidder",
    metaDesc: "Get free estimates from licensed plumbers in your area. Repairs, installations, and emergency plumbing. No spam, verified pros only.",
    heroHeadline: "A $200 Fix Today Beats a $20,000 Water Damage Claim Tomorrow",
    heroSub: "A small leak left unaddressed becomes thousands in structural and mold damage. Get it fixed fast by licensed, verified plumbers.",
    trustPoints: [
      "Licensed master and journeyman plumbers",
      "Fully insured for your protection",
      "No spam — contact goes to one plumber only",
      "Reviews from real customers in your area",
    ],
    process: [
      { step: "01", title: "Describe Your Plumbing Issue", desc: "Tell us what's going on — leak, clog, water heater, repiping, or a bathroom/kitchen rough-in." },
      { step: "02", title: "Receive Plumber Estimates", desc: "Licensed plumbers in your area review your project and send you their bids." },
      { step: "03", title: "Choose Your Plumber", desc: "Compare bids, read reviews, and hire the plumber who fits your timeline and budget." },
    ],
    included: [
      "Leak detection and repair",
      "Water heater installation and repair",
      "Drain cleaning and unclogging",
      "Pipe repair and repiping",
      "Fixture installation (faucets, toilets, showers)",
      "Sewer line repair",
      "Gas line installation",
      "Sump pump installation",
    ],
    persuasiveCopy: "Water doesn't wait. A slow drip behind a wall becomes mold, rot, and structural damage that costs 100x what the fix would have been. The average water damage insurance claim in the U.S. exceeds $12,000 — and that doesn't count what insurance won't cover. Every plumber on MyProjectBidder is licensed, insured, and reviewed by real homeowners. Post your plumbing project now, get bids within hours, and stop the damage before it starts.",
    faqs: [
      { q: "How much does plumbing work cost?", a: "Simple repairs run $150–$500. Water heater replacement: $1,000–$3,000. Repiping a home: $4,000–$15,000. Emergency calls may include an additional service fee." },
      { q: "How fast can a plumber come?", a: "Many plumbers on MyProjectBidder offer same-day or next-day service for urgent issues. Post your project with 'urgent' in the description for faster responses." },
      { q: "Do I need a permit for plumbing work?", a: "Most work beyond simple fixture swaps requires a permit. Licensed plumbers know local codes and can pull permits for you." },
      { q: "How do estimates work?", a: "Post your project for free. Licensed plumbers in your area send you competitive bids. Compare and choose — no obligation, no spam calls." },
    ],
  },
  {
    id: "roofing",
    slug: "roofing",
    title: "Roofing",
    icon: Home,
    shortDesc: "Roof installation and repair",
    heroImage: "https://images.unsplash.com/photo-1635424824849-1b09bdcc55b1?auto=format&fit=crop&w=1200&q=80",
    metaTitle: "Roofing Contractors Near You | Free Estimates — MyProjectBidder",
    metaDesc: "Get free estimates from verified roofing contractors. Repairs, replacements, and inspections. Licensed, insured pros only.",
    heroHeadline: "Your Roof Protects Everything Else You Own",
    heroSub: "Don't leave it to the lowest unverified bidder. Roof failure damages everything underneath it and is expensive to reverse.",
    trustPoints: [
      "Roofing contractors with manufacturer certifications",
      "Licensed, bonded, and fully insured",
      "No storm-chaser spam — real local roofers only",
      "Verified reviews from homeowners near you",
    ],
    process: [
      { step: "01", title: "Describe Your Roofing Needs", desc: "Tell us what you need — repair, replacement, inspection, or a new roof on a build." },
      { step: "02", title: "Receive Roofer Estimates", desc: "Verified roofing contractors in your area review your project and send bids." },
      { step: "03", title: "Choose Your Roofer", desc: "Compare estimates, check warranties and reviews, and hire with confidence." },
    ],
    included: [
      "Full roof replacement",
      "Roof repair and patching",
      "Shingle, metal, and flat roof systems",
      "Storm damage repair",
      "Gutter installation and repair",
      "Roof inspections",
      "Flashing and vent repair",
      "Skylight installation",
    ],
    persuasiveCopy: "Your roof is the single most important structural element of your home — it protects everything underneath from water, wind, heat, and cold. A failing roof doesn't just leak; it damages insulation, drywall, framing, and electrical systems. And roof work done by unverified contractors often voids manufacturer warranties. Every roofer on MyProjectBidder is licensed, insured, and rated by real homeowners. Get bids you can trust before the next storm.",
    faqs: [
      { q: "How much does a new roof cost?", a: "Asphalt shingle roofs run $8,000–$15,000 for an average home. Metal roofs: $12,000–$25,000. Repairs and patches: $300–$1,500 depending on the damage." },
      { q: "How long does roof replacement take?", a: "Most residential roofs are replaced in 1–3 days, weather permitting. Complex roofs or full tear-offs may take longer." },
      { q: "How do I know if I need a new roof?", a: "Signs include missing shingles, leaks, granules in gutters, sagging spots, and age (asphalt shingles last 20–30 years). A free inspection can tell you for sure." },
      { q: "How do I get started?", a: "Post your roofing project on MyProjectBidder for free. Local roofers will review it and send you competitive bids to compare." },
    ],
  },
  {
    id: "landscaping",
    slug: "landscaping",
    title: "Landscaping",
    icon: Trees,
    shortDesc: "Outdoor landscaping and hardscaping",
    heroImage: "https://images.unsplash.com/photo-1759177273737-d891112cc6f3?auto=format&fit=crop&w=1200&q=80",
    metaTitle: "Landscaping Contractors Near You | Free Estimates — MyProjectBidder",
    metaDesc: "Get free estimates from local landscaping pros. Patios, gardens, lawns, and hardscaping. No spam, verified contractors.",
    heroHeadline: "First Impressions Sell Homes — And Outdoor Space Is Your Living Room Extension",
    heroSub: "Landscaping upgrades consistently boost perceived home value and are one of the first things buyers and guests notice.",
    trustPoints: [
      "Landscapers with verified portfolios and reviews",
      "Licensed and insured for your property",
      "No spam calls — one contractor at a time",
      "Real reviews from homeowners in your area",
    ],
    process: [
      { step: "01", title: "Describe Your Outdoor Project", desc: "Tell us what you envision — patio, garden, lawn, retaining wall, outdoor kitchen, or full landscape design." },
      { step: "02", title: "Receive Landscaper Estimates", desc: "Local landscaping pros review your project and send competitive bids." },
      { step: "03", title: "Choose Your Landscaper", desc: "Compare bids, browse portfolios, and hire the landscaper who matches your vision." },
    ],
    included: [
      "Patio and deck design and build",
      "Retaining walls and hardscaping",
      "Garden design and planting",
      "Lawn installation (sod or seed)",
      "Irrigation system installation",
      "Outdoor lighting",
      "Fencing installation",
      "Drainage solutions",
    ],
    persuasiveCopy: "Curb appeal isn't vanity — it's value. Professional landscaping can increase a home's perceived value by 5–12%, and outdoor living spaces are among the fastest-growing categories in home improvement. Whether it's a patio for entertaining, a garden that blooms year-round, or a full backyard transformation, the right landscaper turns dead space into living space. Post your project on MyProjectBidder and get bids from verified local pros.",
    faqs: [
      { q: "How much does landscaping cost?", a: "Basic projects (planting, mulch, sod) run $1,500–$5,000. Patio/hardscaping: $5,000–$20,000. Full landscape designs: $10,000–$50,000+ depending on scope." },
      { q: "How long do landscaping projects take?", a: "Simple planting takes 1–3 days. Patio or hardscaping: 1–2 weeks. Full landscape overhauls: 2–6 weeks depending on complexity." },
      { q: "What time of year is best for landscaping?", a: "Spring and fall are ideal for planting. Hardscaping can be done year-round in most climates. Consult with your landscaper for optimal timing." },
      { q: "How do I get started?", a: "Post your project for free on MyProjectBidder. Local landscapers will review your vision and send you competitive bids." },
    ],
  },
  {
    id: "kitchen-bath",
    slug: "kitchen-bath",
    title: "Kitchen & Bath",
    icon: ChefHat,
    shortDesc: "Kitchen and bathroom remodeling",
    heroImage: "https://images.unsplash.com/flagged/photo-1573168710465-7f7da9a23a15?auto=format&fit=crop&w=1200&q=80",
    metaTitle: "Kitchen & Bath Remodeling Contractors | Free Estimates — MyProjectBidder",
    metaDesc: "Get free estimates from verified kitchen and bathroom remodelers. Countertops, cabinets, tile, and full remodels. No spam.",
    heroHeadline: "The Two Rooms That Sell Your Home — Done by Pros Who Won't Blow Your Budget",
    heroSub: "Kitchens and bathrooms are the top two rooms buyers evaluate and among the best remodel-cost-recouped categories.",
    trustPoints: [
      "Remodelers with proven kitchen and bath portfolios",
      "Licensed, insured, and background-checked",
      "Your contact goes to one contractor — no spam",
      "Real reviews from homeowners who hired them",
    ],
    process: [
      { step: "01", title: "Describe Your Remodel", desc: "Tell us the scope — full kitchen gut, bathroom refresh, countertops, cabinets, tile, or fixtures." },
      { step: "02", title: "Receive Remodeler Estimates", desc: "Verified kitchen and bath pros review your project and send detailed bids." },
      { step: "03", title: "Choose Your Remodeler", desc: "Compare pricing, timelines, and portfolios. Hire the remodeler you trust." },
    ],
    included: [
      "Full kitchen remodels",
      "Bathroom renovations",
      "Countertop installation (granite, quartz, marble)",
      "Cabinet installation and refacing",
      "Tile work (backsplash, shower, floor)",
      "Fixture upgrades (faucets, sinks, showers)",
      "Plumbing and electrical for remodels",
      "Custom vanities and islands",
    ],
    persuasiveCopy: "Kitchens and bathrooms are the two rooms that sell homes. A mid-range kitchen remodel recoups 75–80% of its cost at resale, and updated bathrooms are the single most common request from buyers. But these are also the projects where bad contractor coordination ruins budgets and timelines. MyProjectBidder connects you with verified remodelers who specialize in kitchens and baths — not generalists guessing their way through your most important rooms.",
    faqs: [
      { q: "How much does a kitchen remodel cost?", a: "Minor updates run $10,000–$25,000. Mid-range remodels: $25,000–$60,000. High-end/gut remodels: $60,000–$150,000+. Bathroom remodels: $5,000–$30,000." },
      { q: "How long does a remodel take?", a: "Bathroom refreshes take 2–4 weeks. Full kitchen remodels typically take 6–12 weeks depending on scope and permitting." },
      { q: "Do I need permits for a kitchen or bath remodel?", a: "If you're moving plumbing, electrical, or walls — yes. Your contractor will advise on permits and pull them as needed." },
      { q: "How do I get started?", a: "Post your remodel details on MyProjectBidder for free. Verified remodelers in your area will send competitive bids for you to compare." },
    ],
  },
  {
    id: "construction",
    slug: "construction",
    title: "Construction",
    icon: Building2,
    shortDesc: "New construction and building",
    heroImage: "https://images.unsplash.com/photo-1587582423116-ec07293f0395?auto=format&fit=crop&w=1200&q=80",
    metaTitle: "Construction Contractors Near You | Free Estimates — MyProjectBidder",
    metaDesc: "Get free estimates from licensed general contractors and builders. New builds, additions, and structural work. No spam, verified pros.",
    heroHeadline: "From Blueprint to Build — Licensed Builders Who Pull the Right Permits",
    heroSub: "Turning a vision into a permitted, structurally sound reality. The risk of unlicensed general contractors is unpermitted work that complicates future sales.",
    trustPoints: [
      "General contractors with active licenses",
      "Fully bonded and insured",
      "No cold calls — real builders, real bids",
      "Verified reviews from completed projects",
    ],
    process: [
      { step: "01", title: "Describe Your Build", desc: "Tell us the scope — new construction, addition, ADU, garage, structural modification, or commercial build." },
      { step: "02", title: "Receive Builder Estimates", desc: "Licensed general contractors review your plans and send detailed bids." },
      { step: "03", title: "Choose Your Builder", desc: "Compare pricing, experience, and reviews. Hire the builder who fits your project." },
    ],
    included: [
      "New home construction",
      "Room additions and extensions",
      "Accessory dwelling units (ADUs)",
      "Garage construction",
      "Structural modifications",
      "Foundation work",
      "Framing and rough-in",
      "General contracting and project management",
    ],
    persuasiveCopy: "Construction projects are the biggest investment most homeowners make after buying the home itself. And the #1 risk? Unpermitted work by unlicensed contractors that creates legal headaches, fails inspections, and complicates future sales. Every builder on MyProjectBidder holds an active contractor license, pulls the right permits, and carries the insurance required to protect your investment. Post your project and get real bids from builders who do it right.",
    faqs: [
      { q: "How much does new construction cost?", a: "Costs vary widely by region and scope. Home additions run $100–$300/sq ft. New builds: $150–$400/sq ft. ADUs: $100,000–$300,000. Always get multiple bids." },
      { q: "How long does construction take?", a: "Small additions take 2–4 months. Larger builds and new construction: 6–12+ months depending on complexity and permitting." },
      { q: "Do I need an architect?", a: "For major additions and new construction, yes. Many builders on MyProjectBidder work with architects or can recommend one." },
      { q: "How do I get started?", a: "Post your construction project on MyProjectBidder for free. Include plans or sketches if you have them. Licensed builders will review and send competitive bids." },
    ],
  },
  {
    id: "renovation",
    slug: "renovation",
    title: "Renovation",
    icon: Hammer,
    shortDesc: "Home renovation and remodeling",
    heroImage: "https://images.unsplash.com/photo-1649083048770-82e8ffd80431?auto=format&fit=crop&w=1200&q=80",
    metaTitle: "Home Renovation Contractors Near You | Free Estimates — MyProjectBidder",
    metaDesc: "Get free estimates from verified renovation contractors. Whole-home remodels, updates, and upgrades. No spam, competitive bids.",
    heroHeadline: "One Dashboard, Real Bids, No Chaos — Renovate Without Playing Project Manager",
    heroSub: "Renovation projects fail most often from poor contractor coordination, not bad design. Get verified pros who deliver on time and on budget.",
    trustPoints: [
      "Renovation specialists with proven track records",
      "Licensed, insured, and reviewed",
      "One contractor at a time — no lead spam",
      "Real reviews from completed renovations",
    ],
    process: [
      { step: "01", title: "Describe Your Renovation", desc: "Tell us the vision — whole-home update, specific room overhauls, aging-in-place modifications, or investment property flip." },
      { step: "02", title: "Receive Renovation Estimates", desc: "Verified renovation contractors review your project and send detailed bids." },
      { step: "03", title: "Choose Your Contractor", desc: "Compare bids, timelines, and past work. Hire the renovator who matches your vision." },
    ],
    included: [
      "Whole-home renovations",
      "Open concept conversions",
      "Basement finishing",
      "Attic conversions",
      "Aging-in-place modifications",
      "Investment property renovations",
      "Multi-room updates",
      "Historic home restoration",
    ],
    persuasiveCopy: "Whole-home renovations compound value — updated kitchens, baths, flooring, and paint working together create a home that's worth more than the sum of its parts. But renovations fail more often from poor coordination than bad design. When multiple trades need to work in sequence, you need a contractor who manages timelines, subcontractors, and inspections. MyProjectBidder connects you with verified renovation pros who've done it before and have the reviews to prove it.",
    faqs: [
      { q: "How much does a home renovation cost?", a: "Single-room updates run $5,000–$25,000. Multi-room renovations: $25,000–$100,000. Whole-home gut renovations: $100,000–$300,000+ depending on scope and finishes." },
      { q: "How long does a renovation take?", a: "Small projects take 2–6 weeks. Whole-home renovations typically take 3–6 months. Phased renovations can be planned around your living situation." },
      { q: "Can I live in my home during renovation?", a: "For smaller projects, usually yes. For whole-home guts, most homeowners arrange temporary housing. Your contractor can advise on phasing." },
      { q: "How do I get started?", a: "Post your renovation details on MyProjectBidder for free. Describe the rooms and scope. Verified contractors will send you competitive bids to compare." },
    ],
  },
];

export default servicesData;
